import React, { FC, useState } from 'react';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { message } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { MedicalHistoryValues } from '../types/flow';
import NavRow from '../../assessment-portal/components/navigation-row';
import useScrollToTopOnChange from '../../assessment-portal/hooks/use-scroll-to-top-on-change';
import PersonalBackgroundForm from '../components/personal-background-form';
import ClinicalHistoryForm from '../components/clinical-history-form';
import HealthMedicationsForm from '../components/health-medications-form';
import DocumentUploadForm from '../components/document-upload-form';
import { UPLOAD_ENDPOINT } from '../data/constants';

interface MedicalHistoryPageProps {
  defaultValues: MedicalHistoryValues;
  onBack: () => void;
  onNext: (_values: MedicalHistoryValues) => void;
}

const UPLOAD_ACTION = `${process.env.REACT_APP_API_BASE_URL}${UPLOAD_ENDPOINT}`;
const UPLOAD_HEADERS = {
  'X-API-KEY': process.env.REACT_APP_API_KEY as string,
  'Site-Id': process.env.REACT_APP_CURRENT_SITE_ID || '',
};

const MedicalHistoryPage: FC<MedicalHistoryPageProps> = ({
  defaultValues,
  onBack,
  onNext,
}) => {
  const [step, setStep] = useState(0);
  useScrollToTopOnChange(step);

  const methods = useForm<MedicalHistoryValues>({
    defaultValues: defaultValues || {
      occupation: '',
      marital: '',
      children: '',
      living: '',
      seenSpecialist: [],
      seenPsychiatrist12m: '',
      psychiatristDetail: '',
      reasonForConsult: [],
      reasonOther: '',
      declinedDiagnosis: '',
      prevReportFile: null,
      prevDiagnosis: [],
      otherDiagnosis: '',
      consultReasonNotReferral: [],
      consultReasonNotReferralOther: '',
      familyHistory: '',
      physicalConditions: '',
      medications: '',
      substances: '',
      docs: [null, null, null],
    },
    mode: 'onChange',
  });

  const {
    control, watch, setValue, handleSubmit,
  } = methods;
  const values = watch();

  const [uploadingPrev, setUploadingPrev] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState<Record<number, boolean>>({});

  const handlePrevReportUpload = (info: UploadChangeParam) => {
    const { file } = info;
    if (file.status === 'uploading') {
      setUploadingPrev(true);
    } else if (file.status === 'done') {
      setUploadingPrev(false);
      const resp = file.response || {};
      setValue('prevReportFile', { fileId: resp.fileId || resp.id || '', fileName: resp.fileName || file.name });
    } else if (file.status === 'error') {
      setUploadingPrev(false);
      message.error('File upload failed');
    }
  };

  const handleDocUpload = (index: number, info: UploadChangeParam) => {
    const { file } = info;
    if (file.status === 'uploading') {
      setUploadingDoc((prev) => ({ ...prev, [index]: true }));
    } else if (file.status === 'done') {
      setUploadingDoc((prev) => ({ ...prev, [index]: false }));
      const resp = file.response || {};
      const d = [...values.docs];
      d[index] = { fileId: resp.fileId || resp.id || '', fileName: resp.fileName || file.name };
      setValue('docs', d);
    } else if (file.status === 'error') {
      setUploadingDoc((prev) => ({ ...prev, [index]: false }));
      message.error('File upload failed');
    }
  };

  const toggleArr = (field: keyof MedicalHistoryValues, item: string, exclusive: string[] = []) => {
    const current = (values[field] as string[]) || [];

    if (_.includes(exclusive, item)) {
      setValue(field, _.includes(current, item) ? [] : [item] as any);
      return;
    }

    const withoutExclusive = _.without(current, ...exclusive);
    const next = _.includes(withoutExclusive, item)
      ? _.without(withoutExclusive, item)
      : [...withoutExclusive, item];

    setValue(field, next as any);
  };

  const handleFinalSubmit = handleSubmit((data) => {
    onNext(data);
  });

  const isAnyUploading = uploadingPrev || Object.values(uploadingDoc).some(Boolean);

  const page1Valid = values.occupation && values.marital && values.children && values.living;
  const page2Valid = values.seenPsychiatrist12m && values.reasonForConsult.length > 0 && values.declinedDiagnosis && values.prevDiagnosis.length > 0 && values.consultReasonNotReferral.length > 0;
  const page3Valid = values.familyHistory && values.physicalConditions && values.medications && values.substances;

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-none md:rounded-2xl shadow-sm border border-slate-200 p-4 md:p-8">

      {step === 0 && (
        <>
          <PersonalBackgroundForm control={control} />
          <NavRow
            onBack={onBack}
            onNext={() => setStep(1)}
            nextDisabled={!page1Valid}
            nextLabel="Continue →"
            hint={page1Valid ? undefined : 'Fill in all fields to continue'}
          />
        </>
      )}

      {step === 1 && (
        <>
          <ClinicalHistoryForm
            control={control}
            values={values}
            setValue={setValue}
            toggleArr={toggleArr}
            handlePrevReportUpload={handlePrevReportUpload}
            isUploading={uploadingPrev}
            uploadAction={UPLOAD_ACTION}
            uploadHeaders={UPLOAD_HEADERS}
          />
          <NavRow
            onBack={() => setStep(0)}
            onNext={() => setStep(2)}
            nextDisabled={!page2Valid || isAnyUploading}
            nextLabel="Continue →"
            hint={page2Valid ? undefined : 'Answer all questions to continue'}
          />
        </>
      )}

      {step === 2 && (
        <>
          <HealthMedicationsForm control={control} />
          <NavRow
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextDisabled={!page3Valid}
            nextLabel="Continue →"
            hint={page3Valid ? undefined : 'Fill in all fields to continue'}
          />
        </>
      )}

      {step === 3 && (
        <>
          <DocumentUploadForm
            values={values}
            setValue={setValue}
            handleDocUpload={handleDocUpload}
            uploadingDoc={uploadingDoc}
            uploadAction={UPLOAD_ACTION}
            uploadHeaders={UPLOAD_HEADERS}
          />
          <NavRow
            onBack={() => setStep(2)}
            onNext={handleFinalSubmit}
            nextDisabled={isAnyUploading}
            nextLabel="Book Appointment →"
          />
        </>
      )}
    </div>
  );
};

export default MedicalHistoryPage;
